import { HasAccess } from "@permify/react-role";
import { Button, Col, Empty, Form, Input, message, Popover, Row, Spin, Tabs, Tag, Tooltip } from 'antd';
import moment from 'moment';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import usePaymentAmount from '../../hooks/usePaymentAmount';
import { getMercadopagoUrl, createTokuInvoice } from '../../services/payment';
import {
  getCanceledPatients,
  getPatients,
  getReservation,
  getServicesSummary,
  reservationToConfirm,
  reservationToDraft
} from '../../services/reservation';
import evaluationTypes from "../../utils/evaluationType.json";
import codes from '../../utils/providerCodes.json';
import Notes from '../components/Notes';
import { getCookie } from './../../services/cookies';
import Header from './components/Header';
import PatientsWithForecast from './components/PatientsWithForecast';
import ServicesSummary from './components/ServicesSummary';
import countrySettings from './countrySettings.json';
import useForecasts from './hooks/useForecasts';
import usePaymentMethods from './hooks/usePaymentMethods';

const { format } = require('rut.js');

const ReservationDetailPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState({ fetch: false, send: false, reservation: false, patients: false });
  const [confirmLoading, setConfirmLoading] = useState({ fetch: false, send: false, payment: false });
  const [reservation, setReservation] = useState();
  const [patients, setPatients] = useState();
  const [canceledPatients, setCanceledPatients] = useState();
  const [refresh, setRefresh] = useState();
  const [reservationSummary, setReservationSummary] = useState();
  const [currency, setCurrency] = useState();
  const [summaryItems, setSummaryItems] = useState();
  const [summaryTotal, setSummaryTotal] = useState();
  const [secondaryEmail, setSecondaryEmail] = useState('');
  const { setCompany, paymentData } = usePaymentMethods();
  const { paymentAmount, isLoading: isLoadingPaymentAmount } = usePaymentAmount(reservation);
  let navigate = useNavigate();
  let location = useLocation();
  const { t } = useTranslation();
  const cookieBranchOfficeId = getCookie("branch_office_id");
  const countryCode = getCookie('country_code');
  const forecastData = useForecasts(id, null)

  const fetchReservation = useCallback(async () => {
    if (cookieBranchOfficeId) {
      setLoading(loading => { return { ...loading, fetch: true, reservation: true } });
      try {
        const response = await getReservation({ reservation_id: id }, cookieBranchOfficeId);
        setReservation(response[0]);
        setCurrency({ locateCode: response[0].locate_code, currencyCode: response[0].currency_code });
      } catch (error) {
        console.log(error);
        message.error({
          content: `Error interno ${error}`,
          style: {
            marginTop: '32vh',
            fontSize: 16
          }
        });
      } finally {
        setLoading((loading) => ({ ...loading, fetch: false, reservation: false }));
      }
    }
  }, [id, refresh]);

  const fetchPatients = useCallback(async () => {
    if (cookieBranchOfficeId) {
      setLoading(loading => { return { ...loading, fetch: true, patients: true } });
      try {
        let response = await getPatients({ reservation_id: id }, cookieBranchOfficeId);
        if (response.length === 0) {
          const response = await getServicesSummary({ reservation_id: id });
          setReservationSummary(response);
        } else {
          setPatients(response);
        }
      } catch (error) {
        console.log(error);
        message.error({
          content: `Error interno ${error}`,
          style: {
            marginTop: '32vh',
            fontSize: 16
          }
        });
      } finally {
        setLoading((loading) => ({ ...loading, fetch: false, patients: false }));
      }
    }
  }, [id, refresh]);

  const fetchCanceledPatients = useCallback(async () => {
    if (cookieBranchOfficeId) {
      setLoading(loading => ({ ...loading, fetch: true, patients: true }));
      try {
        let response = await getCanceledPatients({ reservation_id: id });
        setCanceledPatients(response);
      } catch (error) {
        console.error('Error getCanceledPatients:', error);
        message.error({
          content: `Error al obtener pacientes cancelados ${error}`,
          style: { marginTop: '32vh', fontSize: 16 }
        });
      } finally {
        setLoading(loading => ({ ...loading, fetch: false, patients: false }));
      }
    }
  }, [id, cookieBranchOfficeId]);

  const groupByService = useCallback(() => {
    const prestationObj = {};
    let totalValue = 0;

    patients.forEach(item => {
      if (item.status === 'canceled') return;
      item.prestations.forEach(prestation => {
        let prestationAmount = parseFloat(prestation.sale_amount) || 0;

        if (countrySettings.applyConditionalPricing[countryCode]) {
          if (prestation.removedExamIds?.length > 0) {
            const removedExams = (prestation.battery_exams || []).filter(e => prestation.removedExamIds.includes(e.id));
            let removedExamsTotal = 0;
            for (const e of removedExams) {
              removedExamsTotal += parseFloat(e.sale_amount) || 0;
            }
            prestationAmount = Math.max(0, prestationAmount - removedExamsTotal);
          }

          if (prestation.additionals?.length > 0) {
            let additionalsTotal = 0;
            for (const item of prestation.additionals) {
              additionalsTotal += parseFloat(item.sale_amount) || 0;
            }
            prestationAmount += additionalsTotal;
          }
        }

        if (!prestationObj[prestation.name]) {
          prestationObj[prestation.name] = {
            id: `${prestation.id}-${prestation.type}`,
            unit_price: prestationAmount,
            sale_amount: 0,
            quantity: 0,
          };
        }

        const oldPrestation = prestationObj[prestation.name];

        prestationObj[prestation.name] = {
          ...oldPrestation,
          sale_amount: parseFloat(oldPrestation.sale_amount) + prestationAmount,
          quantity: oldPrestation.quantity + 1,
        };

        totalValue += prestationAmount;
      });
    });

    const prestationArray = Object.keys(prestationObj).map(prestationName => {
      return {
        ...prestationObj[prestationName],
        name: prestationName
      }
    });

    const locateCode = currency ? currency.locateCode : 'es-CL';
    const currencyCode = currency ? currency.currencyCode : 'CLP';

    setSummaryItems(prestationArray);
    setSummaryTotal({ totalValue, locateCode, currencyCode });

  }, [patients, currency, countryCode]);

  useEffect(() => {
    fetchReservation();
    fetchPatients();
  }, [fetchReservation, fetchPatients]);

  useEffect(() => {
    fetchCanceledPatients();
  }, [fetchCanceledPatients]);

  useEffect(() => {
    if (patients) {
      groupByService();
    }
  }, [groupByService, patients]);

  useEffect(() => {
    if (reservation && reservation.company_id) setCompany(reservation.company_id);
  }, [reservation, setCompany])

  const formatDate = (date) => {
    return moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY');
  };

  const calcTotalAmount = () => {
    let totalValue = 0;

    reservationSummary.forEach(service => {
      totalValue = totalValue + (parseInt(service.amount) * service.quantity);
    });

    return totalValue;
  }

  const returnToConfirm = async () => {
    try {
      setConfirmLoading(loading => { return { ...loading, send: true } });
      await reservationToConfirm({ reservation_id: id, action: 'confirm' });

      message.success({
        content: `${t('reservations.detailPage.reservation')} confirmada`,
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });
      setLoading(loading => { return { ...loading, send: false } });
      setRefresh(new Date().getUTCMilliseconds());
    } catch (err) {
      console.error(err);
      message.error({
        content: `Error interno ${err}`,
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });
      setLoading(loading => { return { ...loading, send: false } });
    }
  }

  const returnToDraft = async () => {
    try {
      setLoading(loading => { return { ...loading, send: true } });
      const response = await reservationToDraft({ reservation_id: id });

      if (response) {
        message.success({
          content: `${t('reservations.detailPage.reservation')} devuelta a borrador exitosamente`,
          style: {
            marginTop: '32vh',
            fontSize: 16
          }
        });
        setLoading(loading => { return { ...loading, send: false } });
        navigate({ pathname: `/reservas/${id}/editar`, state: { from: location.pathname, search: location.search } });
      } else {
        message.error({
          content: `Ocurrió un error. Inténtalo nuevamente`,
          style: {
            marginTop: '32vh',
            fontSize: 16
          }
        });
        setLoading(loading => { return { ...loading, send: false } });
      }

    } catch (err) {
      console.error(err);
      message.error({
        content: `Error interno ${err}`,
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });
      setLoading(loading => { return { ...loading, send: false } });
    }
  };

  const sendPayment = async () => {
    try {
      if (reservation.payment_methods_codes.includes(codes.mercadopagocl)) {
        setConfirmLoading(state => ({ ...state, payment: true }));

        if (secondaryEmail !== '' && !secondaryEmail.match(/^\S+@\S+\.\S+$/)) {
          throw Error('El mail secundario no es válido')
        }

        await getMercadopagoUrl({
          reservationId: reservation.id,
          reservationNumber: reservation.number,
          items: summaryItems.map(item => ({
            id: item.id,
            title: item.name,
            description: item.name,
            quantity: item.quantity,
            unit_price: item.unit_price,
          })),
          companyId: reservation.company_id,
          companyForeigner: reservation.company.business_document_type !== 'rut',
          secondaryEmail: secondaryEmail !== '' ? secondaryEmail : null,
          reservation
        });
        message.success({
          content: `Link de pago enviado exitosamente`,
          style: {
            marginTop: '32vh',
            fontSize: 16
          }
        });
        setConfirmLoading(state => ({ ...state, payment: false }));
      }
    } catch (error) {
      message.error({
        content: `${error ? error : 'Error Interno'}`,
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });
      setConfirmLoading(state => ({ ...state, payment: false }));
      console.log(error);
    }
  };

  const emitTokuInvoice = async () => {
    try {
      setConfirmLoading(state => ({ ...state, payment: true }));

      await createTokuInvoice({
        companyId: reservation.company_id,
        reservationId: reservation.id,
        amount: reservation.amount_total,
        documentType: reservation.company.business_document_type
      });

      message.success({
        content: 'Factura emitida exitosamente',
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });

      setRefresh(new Date().getUTCMilliseconds());

    } catch (error) {
      console.error(error);
      message.error({
        content: 'Error al emitir la factura.',
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });
    } finally {
      setConfirmLoading(state => ({ ...state, payment: false }));
    }
  };

  const patientsWithForecast = patients && forecastData && PatientsWithForecast({ patients, forecastData })

  const colSpanLarge = reservation?.child_company?.business_name ? 10 : 8;
  const colSpanSmall = reservation?.child_company?.business_name ? 6 : 8;

  const activePatients = useMemo(() => {
    return patients ? patients.filter(patient => patient.status !== 'canceled') : [];
  }, [patients]);

  return (
    <Row justify="center" className="reservation-detail-wrapper mb-80">
      <Col lg={24} xs={24}>
        {
          loading.fetch ?
            <Row justify="center">
              <Spin className="cmt-spin" />
            </Row>
            :
            reservation &&
            <Fragment>
              <Header
                loading={loading}
                setLoading={setLoading}
                reservation={reservation}
                forecastData={forecastData}
                patients={patientsWithForecast}
              />
              <Col lg={23} xs={22}>
                <Tabs className='cmt-tabs' defaultActiveKey="1" items={[
                  {
                    label: 'Datos generales', key: '1', children: <><Row justify="center" className="reservation-detail-wrapper__general-data mt-40">
                      <Col offset={1} lg={23} xs={23}>
                        <Row>
                          <h2 className="mb-20">{t('reservations.detailPage.dataTitle')}</h2>
                        </Row>
                        <Row className="data-container p-30" gutter={[0, 20]}>
                          <Col lg={8} xs={24}>
                            <p className="mb-0">
                              {t('reservations.detailPage.applicantTitle')}:
                              <strong className="ml-5">{reservation.user_fullname}</strong>
                            </p>
                          </Col>
                          <Col lg={colSpanLarge} xs={24}>
                            <p className="mb-0">
                              {t('reservations.detailPage.companyTitle')}:
                              <strong className="ml-5">{reservation.company.business_name}</strong>
                            </p>
                          </Col>
                          {
                            countrySettings.generalData.outsourcing[countryCode] &&
                            <Col lg={colSpanLarge} xs={24}>
                              <p className="mb-0">
                                {t('reservations.detailPage.outsourcingTitle')}:
                                <strong className="ml-5">{reservation.outsourcing?.business_name || reservation.outsourcing?.business_document_number || reservation.outsourcing_id}</strong>
                              </p>
                            </Col>
                          }
                          {
                            countrySettings.generalData.thirdCompany[countryCode] &&
                            <Col lg={colSpanLarge} xs={24}>
                              <p className="mb-0">
                                {t('reservations.detailPage.thirdCompanyTitle')}:
                                <strong className="ml-5">{reservation.third_company?.business_name || reservation.third_company?.business_document_number || reservation.third_company_id}</strong>
                              </p>
                            </Col>
                          }
                          {
                            countrySettings.generalData.invoiceTo[countryCode] &&
                            <Col lg={colSpanLarge} xs={24}>
                              <p className="mb-0">
                                {t('reservations.detailPage.invoiceCompanytitle')}:
                                <strong className="ml-5">{reservation.invoice_company?.business_name || reservation.invoice_company?.business_document_number || reservation.invoice_company_id}</strong>
                              </p>
                            </Col>
                          }
                          {
                            reservation.child_company.business_name &&
                            <Col lg={6} xs={24}>
                              <p className="mb-0">
                                {t('reservations.detailPage.branchOfficeTitle')}:
                                <strong className="ml-5">{reservation.branch_office.name}</strong>
                              </p>
                            </Col>
                          }
                          {
                            reservation?.cost_center?.name && !reservation.child_company.business_name &&
                            <Col lg={8} xs={24}>
                              <p className="mb-0">
                                {t('reservations.detailPage.costCenterTitle')}:
                                <strong className="ml-5">{reservation.cost_center.name}</strong>
                              </p>
                            </Col>
                          }
                          <Col lg={8} xs={24}>
                            <p className="mb-0">
                              {t('reservations.detailPage.contactMethodTitle')}:
                              <strong className="ml-5">
                                {
                                  reservation.contact_method === 'way_phone' && 'Teléfono'
                                }
                                {
                                  reservation.contact_method === 'way_web' && 'Web'
                                }
                                {
                                  reservation.contact_method === 'way_email' && 'Mail'
                                }
                              </strong>
                            </p>
                          </Col>
                          {
                            reservation.child_company.business_name ?
                              <Col lg={10} xs={24}>
                                <p className="mb-0">
                                  {t('reservations.detailPage.subCompanyTitle')}:
                                  <strong className="ml-5">{reservation.child_company.business_name}</strong>
                                </p>
                              </Col>
                              :
                              <Col lg={8} xs={24}>
                                <p className="mb-0">
                                  {t('reservations.detailPage.branchOfficeTitle')}:
                                  <strong className="ml-5">{reservation.branch_office.name}</strong>
                                </p>
                              </Col>
                          }
                          {
                            reservation.reservation_date &&
                            <Col lg={colSpanSmall} xs={24}>
                              <p className="mb-0">
                                {t('reservations.detailPage.dateTitle')}:
                                <strong className="ml-5">{formatDate(reservation.reservation_date)}</strong>
                              </p>
                            </Col>
                          }
                          <Col lg={8} xs={24}>
                            <p className="mb-0">
                              {t('reservations.detailPage.totalPatientsTitle')}:
                              <strong className="ml-5">{reservation.total_patients}</strong>
                            </p>
                          </Col>
                          {
                            reservation?.cost_center?.name && reservation.child_company.business_name &&
                            <Col lg={8} xs={24}>
                              <p className="mb-0">
                                {t('reservations.detailPage.costCenterTitle')}:
                                <strong className="ml-5">{reservation.cost_center.name}</strong>
                              </p>
                            </Col>
                          }
                          {(reservation.company.business_admin_fullname) &&
                            <Col lg={colSpanLarge} xs={24}>
                              <p className="mb-0">
                                {t('reservations.detailPage.companyAdminTitle')}:
                                <strong className="ml-5">{reservation.company.business_admin_fullname}</strong>
                              </p>
                            </Col>
                          }
                          {
                            (reservation.payment_methods.length > 0 && paymentData.length > 0) &&
                            <>
                              <Col lg={colSpanLarge} xs={24}>
                                <p className="mb-0">
                                  {t('reservations.detailPage.paymentMethodTitle')}:
                                  <strong className="ml-5">{paymentData.find(payment => payment.id === reservation.payment_methods[0]).name}</strong>
                                </p>
                              </Col>
                              {
                                (reservation.payment_methods_codes.includes(codes.mercadopagocl) || reservation.payment_methods_codes.includes(codes.toku)) &&
                                <>
                                  <Col lg={8} xs={24}>
                                    <p className="mb-0">
                                      {t('reservations.detailPage.paymentStateTitle')}:
                                      <strong className="ml-5">{reservation.paid ? 'Pagado' : 'No pagado'}</strong>
                                    </p>
                                  </Col>
                                  <Col lg={8} xs={24}>
                                    <p className="mb-0">
                                      Monto pagado:
                                      {
                                        isLoadingPaymentAmount ? (
                                          <span className="payment-amount-spinner">
                                            <Spin size="small" className="cmt-spin" />
                                          </span>
                                        ) : (
                                          <strong className="ml-5">
                                            {paymentAmount?.total
                                              ? `$${paymentAmount.total.toLocaleString('es-CL')}`
                                              : '$0'}
                                          </strong>
                                        )
                                      }
                                    </p>
                                  </Col>
                                </>
                              }
                            </>
                          }
                        </Row>
                      </Col>
                    </Row>

                      {
                        (activePatients?.length > 0) ? (
                          <Fragment>
                            <Row justify="center" className="reservation-detail-wrapper__patients-services mt-60 mb-40">
                              <Col offset={1} lg={24} xs={23}>
                                <Row>
                                  <h2 className="mb-20">{t('reservations.detailPage.patientsAndServicesTitle')}</h2>
                                </Row>
                                <Row className="desktop-title mb-10">
                                  <Col lg={2}>
                                    <p className="ml-15 text-title mb-0">{t('reservations.detailPage.patientTitle')}</p>
                                  </Col>
                                  <Col lg={3}>
                                    <p className="text-center text-title mb-0">{t('reservations.detailPage.statusTitle')}</p>
                                  </Col>
                                  <Col lg={2}>
                                    <p className="text-center text-title mb-0">{t('reservations.detailPage.servicesTitle')}</p>
                                  </Col>
                                  <Col lg={countryCode === 'PE' ? 1 : 2}>
                                    <p className="text-center text-title mb-0">{t('reservations.detailPage.blockTitle')}</p>
                                  </Col>
                                  <Col lg={countryCode === 'PE' ? 3 : 5}>
                                    <p className="text-center text-title mb-0">{t('reservations.detailPage.documentTypeAndNumberTitle')}</p>
                                  </Col>
                                  {
                                    countrySettings.detailPage.showExternalId[countryCode] &&
                                    <Col lg={5}>
                                      <p className="text-center text-title mb-0">{t('reservations.detailPage.externalId')}</p>
                                    </Col>
                                  }
                                  <Col lg={3}>
                                    <p className="text-center text-title mb-0">{t('reservations.detailPage.phoneTitle')}</p>
                                  </Col>
                                  {
                                    countrySettings.detailPage.showProject[countryCode] &&
                                    <Col lg={3}>
                                      <p className="text-center text-title mb-0">{t('reservations.detailPage.projectTitle')}</p>
                                    </Col>
                                  }
                                  <Col lg={4}>
                                    <p className="text-center text-title mb-0">{t('reservations.detailPage.professionTitle')}</p>
                                  </Col>
                                  {
                                    countryCode === 'PE' &&
                                    <Col lg={3}>
                                      <p className="text-center text-title mb-0">Tipo de evaluación</p>
                                    </Col>
                                  }
                                </Row>
                                {
                                  activePatients.map((patient, index) => {
                                    return (
                                      <Row className="patient-item pt-15 pb-15 mb-15" key={patient.id}>
                                        <Col lg={2} xs={12}>
                                          <p className="mobile-title text-center text-title mb-0">
                                            {t('reservations.detailPage.patientTitle')}
                                          </p>
                                          <Col lg={26} xs={26} style={{ display: 'flex', justifyContent: 'center' }}>
                                            <Col>
                                              <p className="text-center mb-0 mr-15">{index + 1}</p>
                                            </Col>
                                            <Col>
                                              <Popover content={
                                                <p className="text-center mb-0">{patient.full_name}</p>
                                              } title="Nombre completo"
                                                placement="rightTop"
                                              >
                                                <i className="icon-system-user" style={{ cursor: "pointer" }} />
                                              </Popover>
                                            </Col>
                                          </Col>
                                        </Col>
                                        <Col lg={3} xs={12}>
                                          <p className="mobile-title text-center text-title mb-0 mt-10">
                                            {t('reservations.detailPage.statusTitle')}
                                          </p>
                                          <Tooltip
                                            trigger="click"
                                            color="#FFFFFF"
                                            title={
                                              <p className="mb-0 text-center">{t(`reservations.detailPage.patientStatusesTooltip.${patient.status}`)}</p>
                                            }
                                          >
                                            <p className="mb-0 text-center" style={{ cursor: "pointer" }}>
                                              {t(`reservations.detailPage.patientStatuses.${patient.status}`)}
                                            </p>
                                          </Tooltip>
                                        </Col>
                                        <Col lg={2} xs={12}>
                                          <Tooltip
                                            trigger="click"
                                            color="#FFFFFF"
                                            title={
                                              <Row justify="center">
                                                <Col lg={24} xs={24}>
                                                  <p className="title mb-5">
                                                    {t('reservations.detailPage.servicesTitle')}
                                                  </p>
                                                </Col>
                                                {
                                                  patient.prestations.map((service, index) => {
                                                    return (
                                                      <Col key={index} lg={24} xs={24}>
                                                        <p className="mb-5">{service.name}</p>
                                                      </Col>
                                                    )
                                                  })
                                                }
                                              </Row>
                                            }
                                          >
                                            <p className="mobile-title text-center text-title mb-0">
                                              {t('reservations.detailPage.servicesTitle')}
                                            </p>
                                            <p className="text-center mb-0" style={{ cursor: "pointer" }}>
                                              {t('reservations.detailPage.seeServicesButton')}
                                            </p>
                                          </Tooltip>
                                        </Col>
                                        <Col lg={countryCode === 'PE' ? 1 : 2} xs={12}>
                                          <p className="mobile-title mb-10 text-center text-title mb-0 mt-10">
                                            {t('reservations.detailPage.blockTitle')}
                                          </p>
                                          <Col lg={24} xs={24} style={{ display: 'flex', justifyContent: 'center' }}>
                                            <Tag>{patient.block}</Tag>
                                          </Col>
                                        </Col>
                                        <Col lg={countryCode === 'PE' ? 3 : 5} xs={12}>
                                          <p className="mobile-title text-center text-title mb-0 mt-10">
                                            {t('reservations.detailPage.documentTypeAndNumberTitle')}
                                          </p>
                                          {
                                            patient.document_type &&
                                            <p className="text-center mb-0">
                                              {
                                                patient.document_type === 'passport'
                                                  ? 'Pasaporte'
                                                  : patient.document_type === 'ce'
                                                    ? 'Carnet de extranjería'
                                                    : patient.document_type.toUpperCase()
                                              }&nbsp;
                                              {patient.document_type === 'rut' ? format(patient.document_number) : patient.document_number}
                                            </p>
                                          }
                                        </Col>
                                        {
                                          countrySettings.detailPage.showExternalId[countryCode] &&
                                          <Col xs={12} lg={5}>
                                            <p className="mobile-title text-center text-title mb-0 mt-10">
                                              {t('reservations.detailPage.externalId')}
                                            </p>
                                            <p className="mb-0 text-center">{patient.external_id && patient.external_id}</p>
                                          </Col>
                                        }
                                        <Col lg={3} xs={12}>
                                          <p className="mobile-title text-center text-title mb-0 mt-10">
                                            {t('reservations.detailPage.phoneTitle')}
                                          </p>
                                          <p className="mb-0 text-center">{patient.phone && patient.phone}</p>
                                        </Col>
                                        {
                                          countrySettings.detailPage.showProject[countryCode] &&
                                          <Col lg={3} xs={12}>
                                            <p className="mobile-title text-center text-title mb-0 mt-10">
                                              {t('reservations.detailPage.projectTitle')}
                                            </p>
                                            <p className="mb-0 text-center">{patient.project_name && patient.project_name}</p>
                                          </Col>
                                        }
                                        <Col lg={4} xs={12}>
                                          <p className="mobile-title text-center text-title mb-0 mt-10">
                                            {t('reservations.detailPage.professionTitle')}
                                          </p>
                                          <p className="mb-0 text-center">{patient.profession && patient.profession}</p>
                                        </Col>
                                        {
                                          countryCode === 'PE' &&
                                          <Col lg={3} xs={12}>
                                            <p className="mobile-title text-center text-title mb-0 mt-10">Tipo de evaluación</p>
                                            <p className="mb-0 text-center">{
                                              (patient.evaluation_type || patient.evaluation_type === 0)
                                              && evaluationTypes[[countryCode]].find((evaluation) => evaluation.value === patient.evaluation_type).text
                                            }</p>
                                          </Col>
                                        }
                                      </Row>
                                    )
                                  })
                                }
                              </Col>
                            </Row>
                          </Fragment>
                        ) : (
                          <Fragment>
                            <Row justify="center" className="mt-50 mb-40">
                              <Col offset={1} lg={23} xs={23}>
                                <Row>
                                  <h2>{t('reservations.detailPage.patientsAndServicesTitle')}</h2>
                                </Row>
                                <Empty
                                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                                  description={
                                    <p>{t('reservations.detailPage.emptyActivePatients')}</p>
                                  }
                                />
                              </Col>
                            </Row>
                          </Fragment>
                        )
                      }

                      {
                        canceledPatients?.length > 0 && (
                          <Row justify="center" className="reservation-detail-wrapper__patients-services mt-30 mb-40">
                            <Col offset={1} lg={24} xs={23}>
                              <Row>
                                <h2 className="mb-20">{t('reservations.detailPage.canceledPatientsTitle', 'Pacientes cancelados')}</h2>
                              </Row>
                              <Row className="desktop-title mb-10">
                                <Col lg={2}>
                                  <p className="ml-15 text-title mb-0">{t('reservations.detailPage.patientTitle')}</p>
                                </Col>
                                <Col lg={countrySettings.detailPage.showProject[countryCode] ? 4 : 5}>
                                  <p className="text-center text-title mb-0">{t('reservations.detailPage.statusTitle')}</p>
                                </Col>
                                <Col lg={countryCode === 'PE' ? 4 : 5}>
                                  <p className="text-center text-title mb-0">{t('reservations.detailPage.documentTypeAndNumberTitle')}</p>
                                </Col>
                                <Col lg={countrySettings.detailPage.showProject[countryCode] ? 3 : 5}>
                                  <p className="text-center text-title mb-0">{t('reservations.detailPage.phoneTitle')}</p>
                                </Col>
                                {
                                  countrySettings.detailPage.showProject[countryCode] &&
                                  <Col lg={3}>
                                    <p className="text-center text-title mb-0">{t('reservations.detailPage.projectTitle')}</p>
                                  </Col>
                                }
                                <Col lg={countrySettings.detailPage.showProject[countryCode] ? 4 : 7}>
                                  <p className="text-center text-title mb-0">{t('reservations.detailPage.professionTitle')}</p>
                                </Col>
                                {
                                  countryCode === 'PE' &&
                                  <Col lg={4}>
                                    <p className="text-center text-title mb-0">Tipo de evaluación</p>
                                  </Col>
                                }
                              </Row>
                              {
                                canceledPatients.map((patient, index) => {
                                  return (
                                    <Row className="patient-item pt-15 pb-15 mb-15" key={patient.id}>
                                      <Col lg={2} xs={12}>
                                        <p className="mobile-title text-center text-title mb-0">
                                          {t('reservations.detailPage.patientTitle')}
                                        </p>
                                        <Col lg={26} xs={26} style={{ display: 'flex', justifyContent: 'center' }}>
                                          <Col>
                                            <p className="text-center mb-0 mr-15">{index + 1}</p>
                                          </Col>
                                          <Col>
                                            <Popover content={
                                              <p className="text-center mb-0">{patient.full_name}</p>
                                            } title="Nombre completo"
                                              placement="rightTop"
                                            >
                                              <i className="icon-system-user" style={{ cursor: "pointer" }} />
                                            </Popover>
                                          </Col>
                                        </Col>
                                      </Col>
                                      <Col lg={countrySettings.detailPage.showProject[countryCode] ? 4 : 5} xs={12}>
                                        <p className="mobile-title text-center text-title mb-0 mt-10">
                                          {t('reservations.detailPage.statusTitle')}
                                        </p>
                                        <Tooltip
                                          trigger="click"
                                          color="#FFFFFF"
                                          title={
                                            <p className="mb-0 text-center">{t(`reservations.detailPage.patientStatusesTooltip.${patient.status}`)}</p>
                                          }
                                        >
                                          <p className="mb-0 text-center" style={{ cursor: "pointer" }}>
                                            {`${t(`reservations.detailPage.patientStatuses.${patient.status === 'canceled' && patient.confirmation_method === 'system' ? 'eliminated' : patient.status}`)}${patient.confirmation_method ? ` - ${patient.confirmation_method}` : ''}`}
                                          </p>
                                        </Tooltip>
                                      </Col>
                                      <Col lg={countryCode === 'PE' ? 4 : 5} xs={12}>
                                        <p className="mobile-title text-center text-title mb-0 mt-10">
                                          {t('reservations.detailPage.documentTypeAndNumberTitle')}
                                        </p>
                                        {
                                          patient.document_type &&
                                          <p className="text-center mb-0">
                                            {
                                              patient.document_type === 'passport'
                                                ? 'Pasaporte'
                                                : patient.document_type === 'ce'
                                                  ? 'Carnet de extranjería'
                                                  : patient.document_type.toUpperCase()
                                            }&nbsp;
                                            {patient.document_type === 'rut' ? format(patient.document_number) : patient.document_number}
                                          </p>
                                        }
                                      </Col>
                                      <Col lg={countrySettings.detailPage.showProject[countryCode] ? 3 : 5} xs={12}>
                                        <p className="mobile-title text-center text-title mb-0 mt-10">
                                          {t('reservations.detailPage.phoneTitle')}
                                        </p>
                                        <p className="mb-0 text-center">{patient.phone && patient.phone}</p>
                                      </Col>
                                      {
                                        countrySettings.detailPage.showProject[countryCode] &&
                                        <Col lg={3} xs={12}>
                                          <p className="mobile-title text-center text-title mb-0 mt-10">
                                            {t('reservations.detailPage.projectTitle')}
                                          </p>
                                          <p className="mb-0 text-center">{patient.project_name && patient.project_name}</p>
                                        </Col>
                                      }
                                      <Col lg={countrySettings.detailPage.showProject[countryCode] ? 4 : 7} xs={12}>
                                        <p className="mobile-title text-center text-title mb-0 mt-10">
                                          {t('reservations.detailPage.professionTitle')}
                                        </p>
                                        <p className="mb-0 text-center">{patient.profession && patient.profession}</p>
                                      </Col>
                                      {
                                        countryCode === 'PE' &&
                                        <Col lg={4} xs={12}>
                                          <p className="mobile-title text-center text-title mb-0 mt-10">Tipo de evaluación</p>
                                          <p className="mb-0 text-center">{
                                            (patient.evaluation_type || patient.evaluation_type === 0)
                                            && evaluationTypes[[countryCode]].find((evaluation) => evaluation.value === patient.evaluation_type).text
                                          }</p>
                                        </Col>
                                      }
                                    </Row>
                                  )
                                })
                              }
                            </Col>
                          </Row>
                        )
                      }

                      <Row justify="center" className="reservation-detail-wrapper__services-summary mt-30 mb-40">
                        <Col offset={1} lg={24} xs={23}>
                          {Array.isArray(summaryItems) && summaryItems.length > 0 ? (
                            <ServicesSummary
                              summaryItems={summaryItems}
                              summaryTotal={summaryTotal}
                              currency={currency}
                            />
                          ) : (
                            <>
                              <Row>
                                <h2 className="mb-20">{t('reservations.detailPage.servicesSummaryTitle')}</h2>
                              </Row>
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={<p>{t('reservations.detailPage.emptySummaryItems')}</p>}
                              />
                            </>
                          )}
                        </Col>
                      </Row>

                      {
                        reservation && reservation.deleted_at === null &&
                        <Row justify="center" className="mt-80">
                          {
                            reservation.payment_methods_codes && reservation.payment_methods_codes.includes(codes.mercadopagocl) && !reservation.paid &&
                            <Col lg={22}>
                              <Row justify='end'>
                                <Col>
                                  <HasAccess
                                    roles={['super_usuario', 'branch_office_manager', 'administracion']}
                                    permissions='reservation-detail'
                                  >
                                    <Form.Item label="Email secundario">
                                      <Input className='cmt-input' placeholder='Ingrese email' value={secondaryEmail} onChange={e => { setSecondaryEmail(e.target.value) }} />
                                    </Form.Item>
                                  </HasAccess>
                                </Col>
                              </Row>
                            </Col>
                          }
                          <Col lg={22} xs={22}>
                            <Row justify="end">
                              <Col xl={4} lg={5} xs={24} className="mr-lg-20 mb-lg-0 mb-md-15 mb-sm-15 mb-xs-15">
                                {
                                  reservation.payment_methods_codes && reservation.payment_methods_codes.includes(codes.mercadopagocl) && !reservation.paid ?
                                    <>
                                      {
                                        reservation.company.isComplete ?
                                          <HasAccess
                                            roles={['super_usuario', 'branch_office_manager', 'administracion']}
                                            permissions='reservation-detail'
                                          >
                                            <Button
                                              loading={confirmLoading.payment}
                                              onClick={sendPayment}
                                              className='cmt-button primary'
                                              type="primary"
                                              size="large"
                                            >
                                              Enviar link de pago
                                            </Button>
                                          </HasAccess>
                                          :
                                          <Popover
                                            trigger="hover"
                                            overlayClassName="sidebar-tooltip"
                                            placement="topLeft"
                                            content={
                                              <p className="text-center mb-0">Debe completar la informaci&oacute;n de la empresa</p>
                                            }
                                          >
                                            <Button
                                              loading={confirmLoading.payment}
                                              onClick={sendPayment}
                                              className='cmt-button primary disabled'
                                              type="primary"
                                              size="large"
                                              disabled
                                            >
                                              {t('reservations.detailPage.sendPaymentLink')}
                                            </Button>
                                          </Popover>
                                      }
                                    </>
                                    : reservation.payment_methods_codes && reservation.payment_methods_codes.includes(codes.toku) &&
                                      reservation.paid && reservation.state === 'confirmed' &&
                                      (!paymentAmount?.details?.toku?.invoices || paymentAmount.details.toku.invoices.length === 0) ?
                                    <>
                                      {
                                        reservation.company.isComplete ?
                                          <HasAccess
                                            roles={['super_usuario', 'branch_office_manager', 'administracion']}
                                            permissions='reservation-detail'
                                          >
                                            <Button
                                              loading={confirmLoading.payment}
                                              onClick={emitTokuInvoice}
                                              className='cmt-button primary'
                                              type="primary"
                                              size="large"
                                            >
                                              Emitir Factura
                                            </Button>
                                          </HasAccess>
                                          :
                                          <Popover
                                            trigger="hover"
                                            overlayClassName="sidebar-tooltip"
                                            placement="topLeft"
                                            content={
                                              <p className="text-center mb-0">Debe completar la informaci&oacute;n de la empresa</p>
                                            }
                                          >
                                            <Button
                                              loading={confirmLoading.payment}
                                              className='cmt-button primary disabled'
                                              type="primary"
                                              size="large"
                                              disabled
                                            >
                                              Emitir Factura
                                            </Button>
                                          </Popover>
                                      }
                                    </>
                                    : reservation.state === 'drafted' ?
                                      <>
                                        {
                                          reservation.company.isComplete ?
                                            <HasAccess
                                              roles={['super_usuario', 'branch_office_manager', 'administracion']}
                                              permissions='reservation-detail'
                                            >
                                              <Button
                                                loading={confirmLoading.send}
                                                onClick={returnToConfirm}
                                                className='cmt-button primary'
                                                type="primary"
                                                size="large"
                                              >
                                                {t('reservations.detailPage.confirmButton')}
                                              </Button>
                                            </HasAccess>
                                            :
                                            <Popover
                                              trigger="hover"
                                              overlayClassName="sidebar-tooltip"
                                              placement="topLeft"
                                              content={
                                                <p className="text-center mb-0">Debe completar la informaci&oacute;n de la empresa</p>
                                              }
                                            >
                                              <Button
                                                loading={confirmLoading.send}
                                                onClick={returnToConfirm}
                                                className='cmt-button primary disabled'
                                                type="primary"
                                                size="large"
                                                disabled
                                              >
                                                {t('reservations.detailPage.confirmButton')}
                                              </Button>
                                            </Popover>
                                        }
                                      </>
                                      :
                                      <HasAccess
                                        roles={['super_usuario', 'branch_office_manager', 'administracion']}
                                        permissions='reservation-detail'
                                      >
                                        <Button
                                          loading={loading.send}
                                          onClick={returnToDraft}
                                          className={`cmt-button primary ${reservation.has_admissions && 'disabled'}`}
                                          disabled={reservation.has_admissions}
                                          type="primary"
                                          size="large"
                                        >
                                          {t('reservations.detailPage.sendToDraftsButton')}
                                        </Button>
                                      </HasAccess>
                                }
                              </Col>
                              <Col xl={4} lg={5} xs={24}>
                                <Link to={{ pathname: `/reservas/${reservation.id}/editar`, state: { from: location.pathname, search: location.search } }}>
                                  <HasAccess
                                    roles={['super_usuario', 'branch_office_manager', 'administracion']}
                                    permissions='reservation-detail'
                                  >
                                    <Button className="cmt-button primary outline" type="primary" size='large'>
                                      {t('reservations.detailPage.editDataButton')}
                                    </Button>
                                  </HasAccess>
                                </Link>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      }</>
                  },
                  { label: 'Notas internas', key: '2', children: <Notes id={id} type="reservation" edit={true} /> },
                ]} />
              </Col>
            </Fragment >
        }
      </Col >
    </Row >
  );
};

export default ReservationDetailPage;
